<template>
  <div class="security-settings-page rightPage">
    <!-- 顶部操作栏 -->
    <div class="security-header">
      <div class="header-left">
        <el-select
          v-if="showCompanySelect"
          v-model="currentCompanyId"
          filterable
          :placeholder="$t('account_redesign.sec_select_company')"
          style="width: 230px; margin-right: 15px"
          @change="onCompanyChange"
        >
          <el-option
            v-for="c in filteredCompanyOptions"
            :key="c.id"
            :value="String(c.id)"
            :label="c.name"
          />
        </el-select>
        <span v-else class="company-text">{{ $t('account_redesign.sec_company_label', { name: currentCompanyName }) }}</span>
        <el-input
          v-model="searchKeyword"
          :placeholder="$t('account_redesign.sec_search_group')"
          clearable
          style="width: 240px"
          @keyup.enter.native="fetchGroups"
          @clear="fetchGroups"
        >
          <i slot="prefix" class="el-input__icon el-icon-search" style="color:#C0C4CC"></i>
        </el-input>
      </div>
      <div class="header-right">
        <el-button type="primary" @click="openCreateDialog">
          <i class="el-icon-plus" style="margin-right: 4px; color: #fff"></i>{{ $t('account_redesign.sec_new_group') }}
        </el-button>
      </div>
    </div>

    <!-- 主体：左分组列表 + 右详情 -->
    <div class="security-body">
      <!-- 左侧分组列表 -->
      <div class="group-sider">
        <div class="sider-head">
          <span>{{ $t('account_redesign.sec_group_list') }}</span>
          <span class="sider-count">{{ groupTotal }} {{ $t('account_redesign.sec_group_unit') }}</span>
        </div>
        <div class="sider-list" v-loading="groupLoading">
          <div
            v-for="item in groupList"
            :key="item.id"
            class="sider-item"
            :class="{ active: selectedGroupId === item.id }"
            @click="selectGroup(item.id)"
          >
            <span class="item-name">
              <i class="el-icon-folder-opened" style="margin-right: 6px"></i>{{ item.name }}
            </span>
            <span class="item-count">{{ item.memberCount || 0 }}</span>
          </div>
          <div v-if="!groupLoading && groupList.length === 0" class="sider-empty">{{ $t('account_redesign.sec_no_group') }}</div>
        </div>
      </div>

      <!-- 右侧主区域 -->
      <div class="group-main" v-loading="detailLoading">
        <template v-if="selectedGroupDetail">
          <!-- 标题栏 -->
          <div class="group-dhead">
            <div class="group-title">
              <h2>{{ selectedGroupDetail.name }}</h2>
              <el-tag :type="selectedGroupDetail.status === 1 ? 'success' : 'info'" size="small">
                {{ selectedGroupDetail.status === 1 ? $t('account_redesign.enable') : $t('account_redesign.disable') }}
              </el-tag>
              <span class="title-meta" v-if="selectedGroupDetail.createTime">
                {{ $t('account_redesign.sec_created_at', { time: formatTime(selectedGroupDetail.createTime) }) }}
              </span>
            </div>
          </div>

          <!-- 4 个 Tab -->
          <el-tabs v-model="activeTab" class="group-tabs" @tab-click="onTabClick">
            <!-- 基本信息 -->
            <el-tab-pane :label="$t('account_redesign.sec_tab_basic')" name="basic">
              <el-form :model="basicForm" label-width="120px" class="basic-form">
                <el-form-item :label="$t('account_redesign.sec_group_name')">
                  <el-input v-model="basicForm.name" style="width: 320px" />
                </el-form-item>
                <el-form-item :label="$t('account_redesign.description')">
                  <el-input
                    v-model="basicForm.description"
                    type="textarea"
                    :rows="3"
                    style="width: 320px"
                  />
                </el-form-item>
                <el-form-item :label="$t('account_redesign.status')">
                  <el-switch
                    v-model="basicForm.status"
                    :active-value="1"
                    :inactive-value="0"
                  />
                </el-form-item>
                <el-form-item :label="$t('account_redesign.create_time')">
                  <span class="form-text">{{ formatTime(selectedGroupDetail.createTime) }}</span>
                </el-form-item>
                <el-form-item :label="$t('account_redesign.scope')">
                  <span class="form-text">{{ selectedGroupDetail.scopeName || $t('account_redesign.sec_not_assoc') }}</span>
                </el-form-item>
                <el-form-item :label="$t('account_redesign.role')">
                  <span class="form-text">{{ selectedGroupDetail.roleName || $t('account_redesign.sec_not_assoc') }}</span>
                </el-form-item>
                <el-form-item>
                  <el-button type="primary" :loading="basicSaving" @click="saveBasic">{{ $t('account_redesign.save') }}</el-button>
                </el-form-item>
              </el-form>
            </el-tab-pane>

            <!-- 范围 -->
            <el-tab-pane :label="$t('account_redesign.scope')" name="scope">
              <div class="scope-role-container">
                <el-form label-width="120px">
                  <el-form-item :label="$t('account_redesign.scope')">
                    <div class="select-with-refresh">
                      <el-select
                        v-model="scopeForm.scopeId"
                        filterable
                        clearable
                        :placeholder="$t('account_redesign.sec_select_scope')"
                        style="width: 300px"
                        @change="onScopeChange"
                      >
                        <el-option
                          v-for="item in scopeOptions"
                          :key="item.id"
                          :value="item.id"
                          :label="item.name"
                        />
                      </el-select>
                      <el-button
                        circle
                        icon="el-icon-refresh"
                        size="mini"
                        @click="loadScopes"
                        :loading="scopeLoading"
                      />
                    </div>
                    <div class="link-wrapper">
                      <router-link :to="{ path: '/account/scope' }" target="_blank" class="add-link">{{ $t('account_redesign.no_scope_add') }}</router-link>
                    </div>
                  </el-form-item>
                  <el-form-item v-if="displayTree.length > 0" :label="$t('account_redesign.organization')">
                    <div class="treeClass" v-loading="scopeTreeLoading">
                      <el-tree :props="treeProps" :data="displayTree" :default-expand-all="true" node-key="sceneId" />
                    </div>
                  </el-form-item>
                </el-form>
              </div>
            </el-tab-pane>

            <!-- 角色 -->
            <el-tab-pane :label="$t('account_redesign.role')" name="role">
              <div class="scope-role-container">
                <el-form label-width="120px">
                  <el-form-item :label="$t('account_redesign.role')">
                    <div class="select-with-refresh">
                      <el-select
                        v-model="roleForm.roleId"
                        filterable
                        clearable
                        :placeholder="$t('account_redesign.sec_select_role')"
                        style="width: 300px"
                        @change="onRoleChange"
                      >
                        <el-option
                          v-for="item in roleOptions"
                          :key="item.id"
                          :value="item.id"
                          :label="item.name"
                        />
                      </el-select>
                      <el-button
                        circle
                        icon="el-icon-refresh"
                        size="mini"
                        @click="loadRoles"
                        :loading="roleLoading"
                      />
                    </div>
                    <div class="link-wrapper">
                      <router-link :to="{ path: '/account/role' }" target="_blank" class="add-link">{{ $t('account_redesign.no_role_add') }}</router-link>
                    </div>
                  </el-form-item>
                  <el-form-item v-if="roleResourceTree.length > 0" :label="$t('account_redesign.sec_permissions')">
                    <div class="treeClass" v-loading="roleTreeLoading">
                      <el-tree :key="$i18n.locale" :props="treeProps" :data="roleResourceTree" :default-expand-all="true" node-key="id" :render-content="renderRoleTreeContent" />
                    </div>
                  </el-form-item>
                </el-form>
              </div>
            </el-tab-pane>

            <!-- 账号 -->
            <el-tab-pane :label="$t('account_redesign.sec_tab_account')" name="user">
              <div class="user-tab">
                <div class="user-header">
                  <div class="user-tip">
                    <i class="el-icon-info" style="margin-right: 4px"></i>
                    {{ $t('account_redesign.sec_account_count', { count: memberTotal }) }}
                  </div>
                  <el-button type="primary" size="small" @click="openAddUserDialog">
                    <i class="el-icon-plus" style="margin-right: 4px; color: #fff"></i>{{ $t('account_redesign.sec_add_account') }}
                  </el-button>
                </div>
                <el-table :data="memberList" v-loading="memberLoading" stripe tooltip-effect="dark" style="width: 100%">
                  <el-table-column prop="account" :label="$t('account_redesign.sec_col_account')" show-overflow-tooltip min-width="120" />
                  <el-table-column prop="username" :label="$t('account_redesign.display_name')" show-overflow-tooltip min-width="120" />
                  <el-table-column :label="$t('account_redesign.status')" width="100px">
                    <template #default="{ row }">
                      <el-tooltip :content="getStatusLabel(row.status)" placement="top">
                        <i :class="getStatusIcon(row.status).icon" :style="{ color: getStatusIcon(row.status).color, fontSize: '16px' }"></i>
                      </el-tooltip>
                    </template>
                  </el-table-column>
                  <el-table-column prop="mobile" :label="$t('account_redesign.sec_col_mobile')" show-overflow-tooltip min-width="120" />
                  <el-table-column prop="email" :label="$t('account_redesign.sec_col_email')" show-overflow-tooltip min-width="180" />
                  <el-table-column :label="$t('account_redesign.actions')" width="100px" align="center">
                    <template #default="{ row }">
                      <el-button type="text" icon="el-icon-delete" size="small" :title="$t('account_redesign.delete')" @click="removeMember(row)" />
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-tab-pane>
          </el-tabs>
        </template>

        <!-- 未选中分组时显示空状态 -->
        <div v-else class="empty-state">
          <i class="el-icon-folder-opened"></i>
          <p>{{ $t('account_redesign.sec_tip_select_group') }}</p>
        </div>
      </div>
    </div>

    <!-- 新建分组弹窗 -->
    <modal :title="$t('account_redesign.sec_new_group')" :visible.sync="createDialogVisible" width="600px" :close-on-click-modal="false" append-to-body>
      <el-form :model="createForm" :rules="createRules" ref="createForm" label-width="120px" class="create-form">
        <el-form-item v-if="showCompanySelect" :label="$t('account_redesign.company')" prop="customerId">
          <el-select
            v-model="createForm.customerId"
            filterable
            :placeholder="$t('account_redesign.sec_select_company')"
            style="width: 300px"
          >
            <el-option
              v-for="c in filteredCompanyOptions"
              :key="c.id"
              :value="String(c.id)"
              :label="c.name"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('account_redesign.sec_group_name')" prop="name">
          <el-input v-model.trim="createForm.name" style="width: 300px" :placeholder="$t('account_redesign.sec_input_group_name')" />
        </el-form-item>
        <el-form-item :label="$t('account_redesign.description')">
          <el-input
            v-model="createForm.description"
            type="textarea"
            maxlength="250"
            :autosize="{ minRows: 4, maxRows: 6 }"
            show-word-limit
            style="width: 300px"
            :placeholder="$t('account_redesign.sec_input_desc')"
          />
        </el-form-item>
        <el-form-item :label="$t('account_redesign.status')">
          <el-switch v-model="createForm.status" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button type="primary" @click="createDialogVisible = false">{{ $t('account_redesign.close') }}</el-button>
        <el-button type="primary" :loading="createLoading" @click="confirmCreate">{{ $t('account_redesign.ok') }}</el-button>
      </div>
    </modal>

    <!-- 添加账号弹窗 -->
    <el-dialog :title="$t('account_redesign.sec_add_account_title')" :visible.sync="addUserDialogVisible" width="720px" append-to-body>
      <div class="add-user-dialog">
        <div class="user-filter-bar">
          <el-input
            v-model="userFilterKeyword"
            :placeholder="$t('account_redesign.sec_search_account')"
            clearable
            style="width: 240px"
            @keyup.enter.native="loadCandidateUsers"
            @clear="loadCandidateUsers"
          >
            <i slot="prefix" class="el-input__icon el-icon-search" style="color:#C0C4CC"></i>
          </el-input>
          <el-button @click="loadCandidateUsers">{{ $t('account_redesign.sec_search_btn') }}</el-button>
        </div>
        <el-table
          :data="candidateUsers"
          v-loading="candidateLoading"
          stripe
          tooltip-effect="dark"
          max-height="400"
          :row-class-name="candidateRowClass"
          @selection-change="onCandidateSelectionChange"
        >
          <el-table-column type="selection" width="50" :selectable="isSelectable" />
          <el-table-column prop="account" :label="$t('account_redesign.sec_col_account')" show-overflow-tooltip min-width="120" />
          <el-table-column prop="username" :label="$t('account_redesign.display_name')" show-overflow-tooltip min-width="120" />
          <el-table-column prop="email" :label="$t('account_redesign.sec_col_email')" show-overflow-tooltip min-width="180" />
        </el-table>
        <div class="pagination-wrapper">
          <el-pagination
            background
            layout="total, prev, pager, next"
            :total="candidateTotal"
            :current-page.sync="candidatePage"
            :page-size="20"
            @current-change="loadCandidateUsers"
          />
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="addUserDialogVisible = false">{{ $t('account_redesign.cancel') }}</el-button>
        <el-button
          type="primary"
          :loading="addUserLoading"
          :disabled="candidateSelected.length === 0"
          @click="confirmAddUsers"
        >
          {{ $t('account_redesign.sec_add_btn') }} ({{ candidateSelected.length }})
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
    listSecurityGroups,
    getSecurityGroupDetail,
    createSecurityGroup,
    updateSecurityGroup,
    deleteSecurityGroup,
    assignGroupScope,
    assignGroupRole,
    listSecurityGroupMembers,
    addGroupMembers,
    removeGroupMember,
    listGroupUserIds,
    listSecurityUsers,
    listPresetRoles,
    listScopes,
    listCompanies
} from '@/api/modules/account.js'
import { accountRole, accountStructure } from '@/api/modules/userInfo.js'
import { roleResource } from '@/api/modules/role.js'

export default {
    name: 'SecuritySettings',
    data() {
        return {
            // 分组列表
            groupList: [],
            groupTotal: 0,
            groupLoading: false,
            searchKeyword: '',
            selectedGroupId: null,
            selectedGroupDetail: null,
            detailLoading: false,

            // 当前企业
            currentCompanyId: null,
            companyOptions: [],

            // Tab
            activeTab: 'basic',

            // 基本信息
            basicForm: { name: '', description: '', status: 1 },
            basicSaving: false,

            // 范围
            scopeForm: { scopeId: null },
            scopeOptions: [],
            scopeLoading: false,
            fullTree: [],
            scopeTreeLoading: false,
            treeProps: { label: 'label', children: 'item' },

            // 角色
            roleForm: { roleId: null },
            roleOptions: [],
            roleLoading: false,
            roleResourceTree: [],
            roleTreeLoading: false,

            // 账号
            memberList: [],
            memberTotal: 0,
            memberLoading: false,

            // 状态 icon 映射（对齐 UAM Account Table.vue）
            statusIconMap: {
                1: { icon: 'el-icon-circle-check', color: '#67c23a' },
                0: { icon: 'el-icon-circle-close', color: '#f56c6c' },
                2: { icon: 'el-icon-time', color: '#e6a23c' },
                3: { icon: 'el-icon-lock', color: '#909399' }
            },
            statusLabelMap: {
                1: 'account_redesign.enable',
                0: 'account_redesign.disable',
                2: 'account_redesign.pending',
                3: 'account_redesign.expire'
            },

            // 添加账号弹窗
            addUserDialogVisible: false,
            userFilterKeyword: '',
            candidateUsers: [],
            candidateTotal: 0,
            candidatePage: 1,
            candidateLoading: false,
            candidateSelected: [],
            addUserLoading: false,
            existingUserIds: [],

            // 新建分组弹窗
            createDialogVisible: false,
            createLoading: false,
            createForm: { name: '', description: '', status: 1, customerId: null },
            createRules: {
                name: [{ required: true, message: () => this.$t('account_redesign.sec_input_group_name'), trigger: 'blur' }],
                customerId: [{ required: true, message: () => this.$t('account_redesign.sec_select_company_required'), trigger: 'change' }]
            }
        }
    },
    computed: {
        // 当前用户信息
        userInfo() {
            return this.$store.state.userInfo || {}
        },
        // 当前用户类型（兼容蛇形/驼峰）
        currentUserType() {
            const u = this.userInfo.user || {}
            return u.userType || this.$store.state.accountUserType || ''
        },
        // 是否平台企业
        isPlatform() {
            const scope = this.userInfo.scope || {}
            const cid = scope.customerId
            return String(cid) === '1'
        },
        // 是否显示 Company 下拉（仅平台企业的 ENTERPRISE_ROOT / ENTERPRISE）
        showCompanySelect() {
            const userType = this.currentUserType
            return this.isPlatform && (userType === 'ENTERPRISE_ROOT' || userType === 'ENTERPRISE')
        },
        // 企业下拉过滤：ENTERPRISE 过滤掉 id=1（平台企业自身）
        filteredCompanyOptions() {
            if (this.currentUserType === 'ENTERPRISE') {
                return this.companyOptions.filter(c => String(c.id) !== '1')
            }
            return this.companyOptions
        },
        // 非平台企业时显示的企业名
        currentCompanyName() {
            const scope = this.userInfo.scope || {}
            return scope.customerName || scope.name || ''
        },
        // 角色类型：选中企业为平台(id=1)→2，否则→0
        calcRoleType() {
            return String(this.currentCompanyId) === '1' ? 2 : 0
        },
        // 范围组织架构树（根据选中 scope 的 sceneIds 过滤）
        displayTree() {
            if (!this.fullTree || this.fullTree.length === 0) return []
            if (this.scopeForm.scopeId) {
                const scope = this.scopeOptions.find(s => s.id === this.scopeForm.scopeId)
                if (scope && scope.sceneIds) {
                    return this.filterTreeByIds(this.fullTree, this.parseSceneIds(scope.sceneIds))
                }
            }
            return this.fullTree
        }
    },
    mounted() {
        if (this.isPlatform) {
            this.currentCompanyId = null
            this.loadCompanies()
        } else {
            const scope = this.userInfo.scope || {}
            this.currentCompanyId = String(scope.customerId || '')
            this.fetchGroups()
        }
    },
    methods: {
        renderRoleTreeContent() {
            let createElement = arguments[0]
            let data = arguments[1].data
            let isZh = (this.$i18n.locale || '').indexOf('zh') === 0
            let displayName = isZh ? (data.nameZh || data.label) : (data.nameEn || data.label)
            return createElement('span', displayName)
        },
        // ===== 分组列表 =====
        async fetchGroups() {
            if (!this.currentCompanyId) {
                this.groupList = []
                this.groupTotal = 0
                this.selectedGroupId = null
                this.selectedGroupDetail = null
                return
            }
            this.groupLoading = true
            try {
                const res = await listSecurityGroups({
                    customerId: this.currentCompanyId,
                    keyword: this.searchKeyword,
                    page: 0,
                    size: 200
                })
                if (res && res.result) {
                    // 后端 @SnakeCaseResponse，member_count → memberCount
                    this.groupList = (res.result.result || []).map(g => ({
                        ...g,
                        memberCount: g.memberCount
                    }))
                    this.groupTotal = (res.result.pageInfo && res.result.pageInfo.total) || this.groupList.length
                    if (this.groupList.length > 0 && !this.selectedGroupId) {
                        this.selectGroup(this.groupList[0].id)
                    }
                }
            } finally {
                this.groupLoading = false
            }
        },

        async selectGroup(id) {
            this.selectedGroupId = id
            await this.loadDetail()
        },

        async loadDetail() {
            if (!this.selectedGroupId) return
            this.detailLoading = true
            try {
                const res = await getSecurityGroupDetail(this.selectedGroupId)
                if (res && res.result) {
                    const d = res.result
                    // 后端 @SnakeCaseResponse 返回 snake_case，这里规范化为 camelCase
                    this.selectedGroupDetail = {
                        ...d,
                        createTime: d.createTime,
                        scopeId: d.scopeId,
                        scopeName: d.scopeName,
                        roleId: d.roleId,
                        roleName: d.roleName,
                        createUserName: d.createUserName
                    }
                    this.basicForm = {
                        name: d.name,
                        description: d.description || '',
                        status: d.status
                    }
                    this.scopeForm.scopeId = this.selectedGroupDetail.scopeId
                    this.roleForm.roleId = this.selectedGroupDetail.roleId
                    // 切换分组时重置树数据
                    this.fullTree = []
                    this.roleResourceTree = []
                    this.activeTab = 'basic'
                }
            } finally {
                this.detailLoading = false
            }
        },

        // ===== 平台企业 Company 切换 =====
        async loadCompanies() {
            try {
                const res = await listCompanies({ page: 0, size: 1000 })
                if (res && res.result) {
                    this.companyOptions = Array.isArray(res.result) ? res.result : (res.result.result || [])
                    if (!this.currentCompanyId && this.filteredCompanyOptions.length > 0) {
                        this.currentCompanyId = String(this.filteredCompanyOptions[0].id)
                        await this.fetchGroups()
                    }
                }
            } catch (e) {
                console.error('[SecuritySettings] loadCompanies failed:', e)
            }
        },

        onCompanyChange() {
            this.selectedGroupId = null
            this.selectedGroupDetail = null
            this.fullTree = []
            this.roleResourceTree = []
            this.fetchGroups()
        },

        // ===== 基本信息 =====
        async saveBasic() {
            this.basicSaving = true
            try {
                await updateSecurityGroup(this.selectedGroupId, {
                    name: this.basicForm.name,
                    description: this.basicForm.description,
                    status: this.basicForm.status
                })
                this.$message.success(this.$t('account_redesign.sec_save_success'))
                // 直接更新本地数据，不刷新页面
                this.selectedGroupDetail.name = this.basicForm.name
                this.selectedGroupDetail.description = this.basicForm.description
                this.selectedGroupDetail.status = this.basicForm.status
                const group = this.groupList.find(g => g.id === this.selectedGroupId)
                if (group) group.name = this.basicForm.name
            } catch (e) {
                this.$message.error(e.message || this.$t('account_redesign.sec_save_failed'))
            } finally {
                this.basicSaving = false
            }
        },

        // ===== 范围 =====
        async loadScopes() {
            this.scopeLoading = true
            try {
                const res = await listScopes({
                    customerId: this.currentCompanyId,
                    start: 0,
                    size: 1000
                })
                if (res && res.result) {
                    // listScopes 返回 BaseResult.success(PageResult)，res.result 是 PageResult
                    this.scopeOptions = (res.result.result || res.result) || []
                }
            } finally {
                this.scopeLoading = false
            }
        },

        async loadFullTree() {
            this.scopeTreeLoading = true
            try {
                const res = await accountStructure({
                    type: this.calcRoleType,
                    isEdit: 0,
                    customerId: this.currentCompanyId
                })
                this.fullTree = (res && res.result && res.result.item) || []
            } catch (e) {
                console.error('loadFullTree failed', e)
                this.fullTree = []
            } finally {
                this.scopeTreeLoading = false
            }
        },

        // 解析 scope.sceneIds 字符串为整型数组
        parseSceneIds(sceneIdsStr) {
            if (!sceneIdsStr) return []
            return sceneIdsStr.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n))
        },

        // 按 sceneIds 过滤树
        filterTreeByIds(nodes, ids) {
            const result = []
            if (!nodes) return result
            nodes.forEach(node => {
                const children = this.filterTreeByIds(node.item, ids)
                const isMatch = ids.includes(node.sceneId)
                if (isMatch || children.length > 0) {
                    result.push({ ...node, item: children })
                }
            })
            return result
        },

        async onScopeChange(val) {
            if (!this.selectedGroupId) return
            try {
                await assignGroupScope(this.selectedGroupId, val)
                this.$message.success(val ? this.$t('account_redesign.sec_assoc_scope_success') : this.$t('account_redesign.sec_unassoc_success'))
                // 直接更新本地数据，不刷新页面
                this.selectedGroupDetail.scopeId = val
                const scope = this.scopeOptions.find(s => s.id === val)
                this.selectedGroupDetail.scopeName = scope ? scope.name : ''
            } catch (e) {
                this.$message.error(e.message || this.$t('account_redesign.sec_operate_failed'))
            }
        },

        // ===== 角色 =====
        async loadRoles() {
            this.roleLoading = true
            try {
                const res = await accountRole({
                    type: this.calcRoleType,
                    customerId: this.currentCompanyId
                })
                if (res && res.result) {
                    this.roleOptions = res.result || []
                }
            } finally {
                this.roleLoading = false
            }
        },

        async onRoleChange(val) {
            if (!this.selectedGroupId) return
            try {
                await assignGroupRole(this.selectedGroupId, val)
                this.$message.success(val ? this.$t('account_redesign.sec_assoc_role_success') : this.$t('account_redesign.sec_unassoc_success'))
                // 直接更新本地数据，不刷新页面
                this.selectedGroupDetail.roleId = val
                const role = this.roleOptions.find(r => r.id === val)
                this.selectedGroupDetail.roleName = role ? role.name : ''
                // 选中角色后加载权限树，清空则移除树
                if (val) {
                    await this.loadRoleResource()
                } else {
                    this.roleResourceTree = []
                }
            } catch (e) {
                this.$message.error(e.message || this.$t('account_redesign.sec_operate_failed'))
            }
        },

        async loadRoleResource() {
            if (!this.roleForm.roleId) {
                this.roleResourceTree = []
                return
            }
            this.roleTreeLoading = true
            try {
                const res = await roleResource({
                    id: this.roleForm.roleId,
                    type: this.calcRoleType,
                    isEdit: 0
                })
                if (res && res.result) {
                    this.roleResourceTree = res.result.item || []
                } else {
                    this.roleResourceTree = []
                }
            } catch (e) {
                console.error('loadRoleResource failed', e)
                this.roleResourceTree = []
            } finally {
                this.roleTreeLoading = false
            }
        },

        // ===== 账号 =====
        async loadMembers() {
            if (!this.selectedGroupId) return
            this.memberLoading = true
            try {
                const res = await listSecurityGroupMembers(this.selectedGroupId, {
                    page: 0,
                    size: 1000
                })
                if (res && res.result) {
                    this.memberList = res.result.result || []
                    this.memberTotal = this.memberList.length
                }
            } finally {
                this.memberLoading = false
            }
        },

        // 状态 icon（对齐 UAM Account Table.vue）
        getStatusIcon(status) {
            return this.statusIconMap[status] || this.statusIconMap[0]
        },
        getStatusLabel(status) {
            const key = this.statusLabelMap[status] || this.statusLabelMap[0]
            return this.$t(key)
        },

        onTabClick(tab) {
            if (tab.name === 'scope') {
                if (this.scopeOptions.length === 0) this.loadScopes()
                if (this.fullTree.length === 0) this.loadFullTree()
            } else if (tab.name === 'role') {
                if (this.roleOptions.length === 0) this.loadRoles()
                if (this.roleForm.roleId && this.roleResourceTree.length === 0) this.loadRoleResource()
            } else if (tab.name === 'user') {
                this.loadMembers()
            }
        },

        // 添加账号弹窗
        async openAddUserDialog() {
            this.addUserDialogVisible = true
            this.candidateSelected = []
            this.userFilterKeyword = ''
            this.candidatePage = 1
            try {
                const res = await listGroupUserIds(this.selectedGroupId)
                this.existingUserIds = (res && res.result) || []
            } catch (e) {
                this.existingUserIds = []
            }
            await this.loadCandidateUsers()
        },

        async loadCandidateUsers() {
            this.candidateLoading = true
            try {
                const res = await listSecurityUsers({
                    companyId: this.currentCompanyId,
                    keyword: this.userFilterKeyword,
                    page: this.candidatePage - 1,
                    size: 20
                })
                if (res && res.result) {
                    this.candidateUsers = res.result.result || []
                    this.candidateTotal = (res.result.pageInfo && res.result.pageInfo.total) || this.candidateUsers.length
                }
            } finally {
                this.candidateLoading = false
            }
        },

        onCandidateSelectionChange(val) {
            this.candidateSelected = val
        },

        // 已关联到当前分组的账号不可再选（禁用 checkbox）
        isSelectable(row) {
            return !this.existingUserIds.includes(row.id)
        },

        // 已关联行加灰色 class，便于视觉区分
        candidateRowClass({ row }) {
            return this.existingUserIds.includes(row.id) ? 'existing-row' : ''
        },

        async confirmAddUsers() {
            if (this.candidateSelected.length === 0) return
            this.addUserLoading = true
            try {
                const userIds = this.candidateSelected.map(u => u.id)
                await addGroupMembers(this.selectedGroupId, userIds)
                this.$message.success(this.$t('account_redesign.sec_add_success', { count: userIds.length }))
                this.addUserDialogVisible = false
                // 重新加载成员列表 + 更新分组列表的 memberCount（不刷新整个页面）
                await this.loadMembers()
                const group = this.groupList.find(g => g.id === this.selectedGroupId)
                if (group) {
                    group.memberCount = (group.memberCount || 0) + userIds.length
                }
            } catch (e) {
                this.$message.error(e.message || this.$t('account_redesign.sec_add_failed'))
            } finally {
                this.addUserLoading = false
            }
        },

        async removeMember(row) {
            try {
                await this.$confirm(this.$t('account_redesign.sec_tip_remove', { name: row.username || row.account }), '', {
                    type: 'warning'
                })
                await removeGroupMember(this.selectedGroupId, row.id)
                this.$message.success(this.$t('account_redesign.sec_remove_success'))
                // 直接更新本地数据，不刷新页面
                this.memberList = this.memberList.filter(m => m.id !== row.id)
                this.memberTotal = this.memberList.length
                const group = this.groupList.find(g => g.id === this.selectedGroupId)
                if (group) {
                    group.memberCount = Math.max(0, (group.memberCount || 0) - 1)
                }
            } catch (e) {
                if (e !== 'cancel') this.$message.error(e.message || this.$t('account_redesign.sec_remove_failed'))
            }
        },

        // ===== 新建分组 =====
        openCreateDialog() {
            this.createForm = {
                name: '',
                description: '',
                status: 1,
                // 有 Company 下拉框时默认选中当前企业；否则使用当前企业 ID
                customerId: this.currentCompanyId
            }
            this.createDialogVisible = true
            this.$nextTick(() => {
                if (this.$refs.createForm) this.$refs.createForm.clearValidate()
            })
        },

        async confirmCreate() {
            this.$refs.createForm.validate(async valid => {
                if (!valid) return
                this.createLoading = true
                try {
                    const res = await createSecurityGroup({
                        name: this.createForm.name,
                        description: this.createForm.description,
                        status: this.createForm.status,
                        customerId: this.createForm.customerId
                    })
                    if (!res || !res.state || res.state.code !== 200) {
                        this.$message.error((res && res.state && res.state.msg) || this.$t('account_redesign.sec_create_failed'))
                        return
                    }
                    this.$message.success(this.$t('account_redesign.sec_create_success'))
                    this.createDialogVisible = false
                    if (this.createForm.customerId === this.currentCompanyId) {
                        await this.fetchGroups()
                    }
                } catch (e) {
                    this.$message.error((e && e.message) || this.$t('account_redesign.sec_create_failed'))
                } finally {
                    this.createLoading = false
                }
            })
        },

        // ===== 工具 =====
        formatTime(timestamp) {
            if (!timestamp) return '-'
            const d = new Date(Number(timestamp))
            const pad = n => String(n).padStart(2, '0')
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
        }
    }
}
</script>

<style lang="scss" scoped>
.security-settings-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    box-sizing: border-box;

    .security-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 15px 0 15px;
        flex-shrink: 0;

        .header-left {
            display: flex;
            align-items: center;
        }

        .company-text {
            font-size: 14px;
            color: #303133;
            margin-right: 15px;
            white-space: nowrap;
        }
    }

    .security-body {
        display: flex;
        flex: 1;
        overflow: hidden;
        padding: 10px 15px 10px 15px;
        gap: 12px;
    }

    // 左侧分组列表
    .group-sider {
        width: 230px;
        flex-shrink: 0;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        background: #fff;

        .sider-head {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 12px;
            background: var(--primary-color);
            color: #fff;
            font-size: 13px;
            border-radius: 4px 4px 0 0;

            .sider-count {
                opacity: 0.85;
                font-size: 12px;
            }
        }

        .sider-list {
            flex: 1;
            overflow-y: auto;
            padding: 4px 0;
        }

        .sider-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            cursor: pointer;
            border-bottom: 1px solid #f5f7fa;
            transition: background 0.2s;
            font-size: 13px;

            .item-name {
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                color: #303133;

                // icon 继承父元素颜色（修复 active 状态下 icon 不可见）
                i {
                    color: inherit;
                }
            }

            .item-count {
                background: #f0f2f5;
                color: #909399;
                padding: 1px 8px;
                border-radius: 10px;
                font-size: 11px;
                margin-left: 6px;
            }

            &:hover {
                background: #f5f7fa;
            }

            &.active {
                background: var(--primary-color);
                .item-name {
                    color: #fff;
                }
                .item-count {
                    background: rgba(255, 255, 255, 0.25);
                    color: #fff;
                }
            }
        }

        .sider-empty {
            text-align: center;
            color: #909399;
            padding: 20px 0;
            font-size: 13px;
        }
    }

    // 右侧主区域
    .group-main {
        flex: 1;
        border: 1px solid #ebeef5;
        border-radius: 4px;
        background: #fff;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .group-dhead {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 16px;
            border-bottom: 1px solid #ebeef5;

            .group-title {
                display: flex;
                align-items: center;
                gap: 10px;

                h2 {
                    margin: 0;
                    font-size: 16px;
                    color: #303133;
                }

                .title-meta {
                    color: #909399;
                    font-size: 12px;
                }
            }
        }

        .group-tabs {
            flex: 1;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            padding: 0 16px;

            ::v-deep .el-tabs__content {
                flex: 1;
                overflow-y: auto;
                padding-top: 8px;
            }
        }

        .basic-form {
            max-width: 600px;
            padding: 16px 0;

            .form-text {
                color: #606266;
                font-size: 14px;
            }
        }

        .scope-role-container {
            padding: 16px 0;

            .select-with-refresh {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            // 超链接样式（对齐 WizardDialog.vue）
            .link-wrapper {
                width: 300px;
                text-align: right;
                box-sizing: border-box;
            }

            .add-link {
                display: inline-block;
                margin-top: 4px;
                font-size: 12px;
                color: var(--primary-color);
                text-decoration: none;
                &:hover {
                    text-decoration: underline;
                }
            }

            .treeClass {
                height: 300px;
                overflow: auto;
                width: 100%;
                padding: 10px 0;
                box-sizing: border-box;
                border: 1px solid #e7e7e7;
            }
        }

        .user-tab {
            .user-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 12px;

                .user-tip {
                    color: #606266;
                    font-size: 13px;
                }
            }
        }

        .empty-state {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            color: #909399;

            i {
                font-size: 48px;
                margin-bottom: 12px;
            }

            p {
                font-size: 14px;
            }
        }
    }
}

.add-user-dialog {
    .user-filter-bar {
        display: flex;
        gap: 8px;
        margin-bottom: 12px;
    }

    .pagination-wrapper {
        padding-top: 12px;
        display: flex;
        justify-content: flex-end;
    }
}

// 弹出框 footer 按钮间距
.dialog-footer {
    padding-top: 16px;
    text-align: right;

    .el-button + .el-button {
        margin-left: 12px !important;
    }
}

// New Group 浮动框表单整体横向居中
.create-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 0;

    ::v-deep .el-form-item {
        width: 100%;
        max-width: 420px;
    }
}
</style>

<!-- 非 scoped 样式：el-dialog append-to-body 导致 scoped 选择器无法命中 -->
<style lang="scss">
.add-user-dialog {
    .existing-row {
        background: #f5f7fa !important;
        color: #c0c4cc;

        .el-checkbox__input.is-disabled .el-checkbox__inner {
            background-color: #f5f7fa;
            border-color: #e4e7ed;
        }
    }
}
</style>
